import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import { createTheme, IconButton, Slide, ThemeProvider } from "@mui/material";
import useDeviceName from "components/Devices/detectDevices";
import EhrPatientDataSidebar from "components/sidebars/EhrPatientData";
import BottomNav from "components/wrapper_v2/BottomNav";
import { COLOR_CONSTANTS } from "config/helpers/color_helper";
import {
  getDeviceDetails,
  removeQueryParams,
} from "config/helpers/common_helper";
import { isEqual } from "lodash";
import { SidebarContext } from "mobile/mobileComponents/sidebarContext";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { change_app_language, change_theme } from "redux/actions/AppActions";
import { encounterObject } from "redux/actions/EncounterActions";
import { show_redirect_popup } from "redux/actions/MobileTabActions";
import { set_show_popup } from "redux/actions/ScribeAcitons";
import FirstVisit from "screens/home/components/FirstVisit";
import MobileSideMenu from "./MobileSlideMenu";
import MobileTopbar from "./MobileTopbarNew";
import { isEmpty } from "lodash";
import { getCurrentRecordingState } from "config/helpers/recordingStateSelector";

const MobileWrapper = ({ children }) => {
  const deviceDetails = getDeviceDetails(useDeviceName());
  const path = window.location.pathname;
  const queryParams = new URLSearchParams(window.location.search);
  const visitId = queryParams.get("visitId");
  const reduxDispatch = useDispatch();
  const { openSidebar, closeSidebar, isSidebarOpen } =
    useContext(SidebarContext);
  const app = useSelector((state) => state.app, isEqual);
  const currentUserSettingsData = useSelector(
    (state) => state?.auth?.currentUserSettings
  );
  const patientObj = useSelector((state) => state?.patientObj?.patientObj);
  const encounterObj = useSelector((state) => state?.encounterObj);
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useSelector((state) => state?.app.theme);

  const {
    connectedEHR: { connectionId: connectedEhrId },
    encounterObj: { consolidatedNote },
  } = useSelector((state) => state);

  const showEhrDataSidebar =
    path.includes("patients/visits") &&
    Boolean(connectedEhrId) &&
    Boolean(
      !isEmpty(
        patientObj?.identifier?.find((item) => {
          return item?.system === "external-patient-id";
        })
      )
    );

  const themeLight = useMemo(() => {
    document.documentElement.setAttribute("data-bs-theme", app.theme);
    return createTheme({
      palette: {
        mode: app.theme,
        ...(app.theme === "dark"
          ? {
              textFieldColor: "#387CA3",
              background: {
                default: "#201F48",
                paper: "#201F48",
              },
              primary: {
                main: COLOR_CONSTANTS.PRIMARY,
              },
              dataGridHeaderColor: "#00adb5",
            }
          : {
              textFieldColor: "#387CA3",
              primary: {
                main: COLOR_CONSTANTS.PRIMARY,
              },
              dataGridHeaderColor: "#f8f8f8",
            }),
      },
      // zIndex: {},
      typography: {
        fontFamily: `"Poppins", sans-serif`,
        fontSize: "0.8rem",
        htmlFontSize: "0.8rem",
      },
      components: {
        MuiSvgIcon: {
          styleOverrides: {
            root: {
              fontSize: "1.2rem",
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              fontSize: "1.2rem",
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              fontSize: "0.7rem",
            },
          },
        },
      },
    });
  }, [app.theme]);

  useEffect(() => {
    if (app?.theme !== currentUserSettingsData?.display?.theme) {
      reduxDispatch(change_theme(currentUserSettingsData?.display?.theme));
    }

    if (
      app?.language !== currentUserSettingsData?.display?.language &&
      currentUserSettingsData?.display?.language
    ) {
      i18n.changeLanguage(currentUserSettingsData?.display?.language);
      reduxDispatch(
        change_app_language(currentUserSettingsData?.display?.language)
      );
    }
  }, [currentUserSettingsData?.display, reduxDispatch, i18n, app]);
  const currentUserPracticeData = useSelector(
    (state) => state?.auth?.currentUserPracticeData
  );

  const patientId = useSelector((state) => state.patientObj?.patientObj?.id);
  const encounterId = useSelector((state) => state.encounterObj?.encounter?.id);

  const { recording, paused } = useSelector((state) =>
    getCurrentRecordingState(state, patientId, encounterId)
  );

  return (
    <div
      // className="min-vh-100"
      style={{ position: "relative", height: "100dvh", overflow: "hidden" }}
    >
      <ThemeProvider theme={themeLight}>
        <Slide
          style={{ position: "absolute", width: "100%", zIndex: "2200" }}
          direction="right"
          in={isSidebarOpen("main")} // Uses sidebar ID
          mountOnEnter
          unmountOnExit
        >
          <div>
            <MobileSideMenu />
          </div>
        </Slide>
        <FirstVisit
          isOpen={
            currentUserPracticeData?.practiceData &&
            !(
              currentUserPracticeData?.practiceData?.practice_speciality ||
              currentUserPracticeData?.practiceData?.isFirstVisitSkipped
            ) &&
            !location?.pathname?.includes("/checkout") &&
            !path.includes("/store/add_payment_method") &&
            !path.includes("/store/upgrade_success") &&
            !path.includes("/store/upgrade_failed")
          }
          handleClose={() => {
            if (deviceDetails?.redirectLink) {
              reduxDispatch(show_redirect_popup(true));
            }
          }}
        />
        <div style={{ height: "5dvh", display: "flex", flexDirection: "row" }}>
          {/* Top Component will be here */}
          {path.includes("/patients/visits") ||
          path.includes("/home/support") ||
          path.includes("/home/planparticulars") ? (
            <IconButton
              onClick={() => {
                if (path.includes("/patients/visits")) {
                  if (recording || paused) {
                    // if(visitId){
                    reduxDispatch(
                      set_show_popup(
                        true,
                        `/patients/visits/${patientObj?.id}`,
                      ),
                    );
                    // }
                  } else {
                    if (visitId) {
                      reduxDispatch(encounterObject({}));
                      removeQueryParams(["tabId", "visitId"]);
                    } else {
                      navigate("/patients/list");
                    }
                  }
                } else if (
                  path.includes("/home/support") ||
                  path.includes("/home/planparticulars")
                ) {
                  navigate(-1);
                  openSidebar("main"); // Open the sidebar
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <IconButton onClick={() => openSidebar("main")}>
              <MenuIcon sx={{ color: theme === "dark" ? "#fff" : "#272652" }} />
            </IconButton>
          )}
          <MobileTopbar />
        </div>
        <div
          className="hide-scroll mobile-wrapper-container"
          style={{
            height: path.includes("/patients/visits") ? "95dvh" : "85dvh",
          }}
        >
          {children}
        </div>
        {!(
          path.includes("/patients/visits") ||
          path.includes("/home/support") ||
          path.includes("/home/planparticulars")
        ) && (
          <div style={{ height: "8dvh" }}>
            <BottomNav />
          </div>
        )}
      </ThemeProvider>
      {showEhrDataSidebar ? <EhrPatientDataSidebar /> : null}
    </div>
  );
};

export default MobileWrapper;
